import { Check, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ParentLayout } from "@/components/layout/ParentLayout";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Pill } from "@/components/ui/Pill";
import { useObservable } from "@/hooks/useObservable";
import { formatKr } from "@/lib/format";
import { currentWeekBounds, DEFAULT_WEEK_START_DAY } from "@/lib/week";
import { familyService } from "@/services/family.service";
import { parentWeekService } from "@/services/parentWeek.service";

export function ParentDashboard() {
  const state = useObservable(parentWeekService.state$, {
    weeks: [],
    currentWeek: null,
    awaitingCloseCount: 0,
    loading: true,
  });
  const familyState = useObservable(familyService.state$, {
    members: [],
    settings: null,
    loading: false,
  });
  const navigate = useNavigate();
  const offset = familyState.settings?.week_start_day ?? DEFAULT_WEEK_START_DAY;
  const bounds = currentWeekBounds(offset);
  const week = bounds.iso_week;
  const summary = state.currentWeek;

  const familyName = familyState.settings?.name ?? "Familien";
  const weeksHref = `/parent/weeks/${bounds.year}-${bounds.iso_week}`;

  return (
    <ParentLayout>
      <div style={{ maxWidth: 720 }}>
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: 0.7,
              fontWeight: 700,
            }}
          >
            Uke {week}
          </div>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 34,
              fontWeight: 400,
              marginTop: 2,
              letterSpacing: -0.8,
            }}
          >
            {familyName}
          </div>
        </div>

        {state.loading || !summary ? (
          <div style={{ color: "var(--ink-3)", fontSize: 14 }}>Laster…</div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 20,
              }}
            >
              <Card style={{ padding: 20 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.7,
                    color: "var(--ink-3)",
                    marginBottom: 4,
                  }}
                >
                  Total utbetaling
                </div>
                <div
                  className="num"
                  style={{
                    fontSize: 36,
                    fontWeight: 700,
                    letterSpacing: -1.5,
                    color: "var(--forest)",
                  }}
                >
                  {formatKr(summary.totalPayout)}
                </div>
              </Card>
              <Card style={{ padding: 20 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.7,
                    color: "var(--ink-3)",
                    marginBottom: 4,
                  }}
                >
                  Status
                </div>
                <div style={{ marginTop: 6 }}>
                  {summary.allConfirmed ? (
                    <Pill tone="good">
                      <Check size={12} /> Alle bekreftet
                    </Pill>
                  ) : (
                    <Pill tone="warn">Venter på barn</Pill>
                  )}
                </div>
              </Card>
            </div>

            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.7,
                color: "var(--ink-2)",
                marginBottom: 10,
              }}
            >
              Barna denne uka
            </div>
            <Card padding={0} style={{ overflow: "hidden", marginBottom: 20 }}>
              {summary.children.map((c, i) => (
                <div
                  key={c.child.id}
                  onClick={() => navigate(weeksHref)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "16px 20px",
                    borderBottom:
                      i < summary.children.length - 1
                        ? "1px solid var(--line)"
                        : "none",
                    cursor: "pointer",
                  }}
                >
                  <Avatar person={c.child} size={40} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                      {c.child.data.name}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "var(--ink-3)",
                        marginTop: 2,
                      }}
                    >
                      {c.logs.length} jobber logget
                    </div>
                  </div>
                  <div style={{ textAlign: "right", marginRight: 8 }}>
                    <div
                      className="num"
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: "var(--good)",
                      }}
                    >
                      +{formatKr(c.totalEarned)}
                    </div>
                    <StatusBadge status={c.week?.data.status} />
                  </div>
                  <ChevronRight size={14} color="var(--ink-3)" />
                </div>
              ))}
              {summary.children.length === 0 && (
                <div
                  style={{
                    padding: "24px 20px",
                    color: "var(--ink-3)",
                    fontSize: 14,
                    textAlign: "center",
                  }}
                >
                  Ingen uker registrert ennå
                </div>
              )}
            </Card>

            {summary.allConfirmed && (
              <Button
                variant="primary"
                size="md"
                icon={<Check size={16} color="#fff" />}
                onClick={() => navigate(weeksHref)}
              >
                Gå til ukesoppgjør
              </Button>
            )}
          </>
        )}
      </div>
    </ParentLayout>
  );
}

function StatusBadge({ status }: { status?: string }) {
  if (!status) return null;
  if (status === "open")
    return (
      <Pill tone="warn" size="sm">
        Åpen
      </Pill>
    );
  if (status === "child_confirmed")
    return (
      <Pill tone="coral" size="sm">
        Bekreftet ✓
      </Pill>
    );
  if (status === "closed")
    return (
      <Pill tone="forest" size="sm">
        Lukket
      </Pill>
    );
  return null;
}
